import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { MessageDialogComponent, MessageBoxCustomButton } from 'src/app/angular-core/components/message-dialog/message-dialog.component';
import { LocalizedDatePipe } from 'src/app/angular-core/utils/pipes/localized-date/localized-date.pipe';
import { SubscriptionDetails, SubscriptionState } from 'src/app/generated/CrossPoint.License.Server';
import { BasicXpacInfo, DeviceProblem } from 'src/app/generated/CrossPoint.RemoteXpac.Server';
import { PermissionsService } from 'src/app/services/permissions.service';
import { XpacProxyService } from 'src/app/services/xpac-proxy.service';
import { DevicesActionsTypes, deleteDeviceAction, getDeviceProblemsAction, restartDeviceAction, restartDeviceSuccessAction } from 'src/app/store/actions/devices.actions';
import { LicensesActionsTypes, getLicensesDetailsInfoAction } from 'src/app/store/actions/licenses.actions';
import { selectDeviceProblems } from 'src/app/store/selectors/devices.selectors';
import { selectSubscriptionDetails } from 'src/app/store/selectors/licenses.selectors';
import { OperationEnum } from 'src/app/utils/enums/operation.enums';
import { hasValue } from 'src/app/angular-core/utils/functions/common.functions';
import { hasPermission } from 'src/app/utils/functions/permission.function';
import { getProblemInfoTranslation, getProblemTranslation } from 'src/app/utils/functions/problems.functions';
import { IProblemState, IRemoteXPACPortalState } from 'src/app/utils/interfaces/store.interfaces';

@UntilDestroy()
@Component({
  selector: 'xpac-remote-maintenance-item-general',
  templateUrl: './remote-maintenance-item-general.component.html',
  styleUrls: ['./remote-maintenance-item-general.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RemoteMaintenanceItemGeneralComponent implements OnInit {
  @Input() public xpac: BasicXpacInfo = null;

  public isLoading: boolean = true;
  public isLoadingLicense: boolean = true;
  public isLoadingProblems: boolean = true;
  public storeLicense: SubscriptionDetails = null;
  public currentProblems: DeviceProblem[] = null;

  constructor(private store: Store<IRemoteXPACPortalState>,
    public dialog: MatDialog,
    private datePipe: LocalizedDatePipe,
    private translate: TranslateService,
    private actionsListener$: Actions,
    private cdr: ChangeDetectorRef,
    private permissionsService: PermissionsService,
    private xpacProxyService: XpacProxyService,
    private translatePipe: TranslatePipe,
    private router: Router,
  ) { }

  public ngOnInit(): void {
    this.initListeners();
    this.initData();
  }

  private initListeners(): void {
    this.actionsListener$
      .pipe(ofType(LicensesActionsTypes.GET_LICENSES_DETAILS_INFO_FAILED))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.isLoading = false;
        this.cdr.markForCheck();
      });

    this.actionsListener$
      .pipe(
        ofType(DevicesActionsTypes.RESTART_DEVICE_SUCCESS),
        untilDestroyed(this)
      )
      .subscribe((action: ReturnType<typeof restartDeviceSuccessAction>) => {
        const headerText: string = this.translate.instant('Modules.RemoteMaintenance.Connections.RestartDevice');
        const messageText: string = this.translate.instant('Modules.RemoteMaintenance.Connections.DeviceRestartingMessage', { deviceId: action.data });

        this.dialog.open(MessageDialogComponent, {
          panelClass: 'messagebox-dialog',
          autoFocus: false,
          data: { headerInfo: headerText, message: messageText, buttons: MessageBoxCustomButton.OK }
        });
      });

  }

  private async initData(): Promise<void> {
    const permissions = await this.permissionsService.getPermissions();
    if (hasPermission("RemoteMaintenance_SubscriptionEndDate", permissions, OperationEnum.READ)) {
      this.store.dispatch(getLicensesDetailsInfoAction({ data: this.xpac.deviceId }));

      this.store.select(selectSubscriptionDetails(this.xpac.deviceId))
        .pipe(
          filter(hasValue),
          untilDestroyed(this)
        )
        .subscribe((licenses: SubscriptionDetails) => {
          this.storeLicense = licenses;
          this.isLoadingLicense = false;
          this.checkLoading();
        });
    }
    else {
      this.isLoadingLicense = false;
    }

    if (hasPermission('Device_Problems', permissions, OperationEnum.READ)) {
      this.store.dispatch(getDeviceProblemsAction({ data: this.xpac.deviceId }));

      this.store.select(selectDeviceProblems(this.xpac.deviceId))
        .pipe(
          filter(hasValue),
          untilDestroyed(this)
        )
        .subscribe(({ problems }: IProblemState) => {
          this.currentProblems = problems;
          this.isLoadingProblems = false;
          this.checkLoading();
        });
    }
    else {
      this.isLoadingProblems = false;
    }

    this.checkLoading();
  }

  private checkLoading(): void {
    if (!this.isLoadingLicense && !this.isLoadingProblems && this.isLoading) {
      this.isLoading = false;
    }
    this.cdr.markForCheck();
  }

  public getFirmwareVersion(): string {
    return this.xpac.firmware;
  }

  public getSerial(): string {
    let serial: string = this.translate.instant('General.NotAvailableShort');

    try {
      if (this.storeLicense.subscriptions[0].orderId) {
        serial = this.storeLicense.subscriptions[0].orderId;
      }
    } catch {
    }

    return serial;
  }

  public getEndDate(): string {
    try {

      if (this.storeLicense.subscriptionState === SubscriptionState.Expiring) {
        return this.translatePipe.transform('Modules.Licenses.List.Expired.Expires') + " " + this.datePipe.transform(this.storeLicense.remoteMaintenanceEndsUtc, 'shortDate');
      }
      else if (this.storeLicense.subscriptionState === SubscriptionState.Expired) {
        if (this.storeLicense.remoteMaintenanceEndsUtc) {
          return this.translatePipe.transform('Modules.Licenses.List.Expired.Expired') + " " + this.datePipe.transform(this.storeLicense.remoteMaintenanceEndsUtc, 'shortDate');
        }
        return this.translatePipe.transform('Modules.Licenses.List.NoLicense');
      }
      else if (this.storeLicense.subscriptionState === SubscriptionState.Active) {
        const remoteMaintenanceStartsUtc: Date = new Date(this.storeLicense.remoteMaintenanceStartsUtc);
        const eightMonthsFromRemoteMaintenanceStartsUtc: Date = new Date(remoteMaintenanceStartsUtc.setMonth(remoteMaintenanceStartsUtc.getMonth() + 6));
        return this.translatePipe.transform('Modules.Licenses.List.AutoRenewal') + " " + this.datePipe.transform(eightMonthsFromRemoteMaintenanceStartsUtc.toUTCString(), 'shortDate');
      }
      else {
        return this.translatePipe.transform('Modules.Licenses.List.NoLicense');
      }
    } catch (error) {
      return this.translate.instant('General.NotAvailableShort');
    }
  }

  public deleteXpac(): void {
    const headerText: string = this.translate.instant('Modules.RemoteMaintenance.Connections.RemoveDevice');
    const messageText: string = this.translate.instant('Modules.RemoteMaintenance.Connections.RemoveDeviceMessage')

    const dialogRef: MatDialogRef<MessageDialogComponent> = this.dialog.open(MessageDialogComponent, {
      panelClass: 'messagebox-dialog',
      autoFocus: false,
      data: { headerInfo: headerText, message: messageText, buttons: MessageBoxCustomButton.YesNo }
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.cdr.markForCheck();
        this.store.dispatch(deleteDeviceAction({ data: this.xpac.deviceId }));
      }
    });
  }

  public redirectToRemoteXPAC(): void {
    if (this.xpac.isConnected) {
      try {
        this.xpacProxyService.open(this.xpac.deviceId);
      } catch (error) {
        console.log("error this.xpacProxyService.open: " + error);
      }
    }
  }

  public openXpacManagement(): void {
    this.router.navigate([`xpac-management/xpac-info/${this.xpac.deviceId}`]);
    this.dialog.closeAll();
  }

  public restartXpac(): void {
    if (!this.xpac.isConnected) {
      return;
    }

    const headerText: string = this.translate.instant('Modules.RemoteMaintenance.Connections.RestartDevice');
    const messageText: string = this.translate.instant('Modules.RemoteMaintenance.Connections.RestartDeviceMessage')

    const dialogRef: MatDialogRef<MessageDialogComponent> = this.dialog.open(MessageDialogComponent, {
      panelClass: 'messagebox-dialog',
      autoFocus: false,
      data: { headerInfo: headerText, message: messageText, buttons: MessageBoxCustomButton.YesNo }
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.store.dispatch(restartDeviceAction({ data: this.xpac.deviceId }));
      }
    });
  }

  public getProblemTitle(problem: DeviceProblem): string {
    return getProblemTranslation(problem, this.translate);
  }

  public getProblemInfo(problem: DeviceProblem): string {
    return getProblemInfoTranslation(problem);
  }
}